export type Language = 'ja' | 'en'

export const translations = {
  ja: {
    // Navigation
    nav: {
      dashboard: 'ダッシュボード',
      aiNocManager: 'AI NOCマネージャー',
      alerts: 'アラート',
      aiDefenseLogs: 'AI防衛ログ',
      liveNetwork: 'ライブネットワーク',
      aiWorkbench: 'AIワークベンチ',
      settings: '設定',
    },

    // Header titles
    header: {
      dashboard: 'ダッシュボード',
      aiNocManager: 'AI NOCマネージャー',
      alerts: 'アラート',
      settings: '設定',
      aiWorkbench: 'AIワークベンチ',
      liveNetwork: 'ライブネットワーク',
      aiDefenseLogs: 'AI防衛ログ',
    },

    // Language switcher
    language: {
      switchToEn: 'English',
      switchToJa: '日本語',
    },

    // Dashboard page
    dashboard: {
      greeting: 'おはようございます',
      overviewText: 'ワークスペースの概要をご覧ください',
      export: 'エクスポート',
      preparingExport: 'エクスポートを準備中...',
      exportSuccess: 'ダッシュボードデータのエクスポートが完了しました！',
      exportError: 'データのエクスポートに失敗しました',
      fetchingData: 'データを取得中：',
      updatedTo: '更新しました：',
      timeRanges: {
        today: '今日',
        past7days: '過去7日間',
        past15days: '過去15日間',
        pastMonth: '過去1ヶ月',
        pastYear: '過去1年',
      },
    },

    // Insights/Alerts page
    insights: {
      loadingAlerts: 'アラートを読み込み中...',
      errorLoadingInsights: 'インサイトの読み込みエラー',
      activeIncidentsOverview: 'アクティブインシデント概要',
      totalIncidents: '総インシデント数：',
      solved: '解決済み',
      highPriority: '高優先度',
      mediumPriority: '中優先度',
      overviewFilterAll: 'すべて',
      overviewFilterNoc: 'NOC',
      overviewFilterSoc: 'SOC',
      activeIncidents: 'アクティブインシデント',
      triggerDemoAlerts: 'デモアラートを起動',
      history: '履歴',
      allStatus: '全ステータス',
      new: '新規',
      peerReview: 'ピアレビュー',
      resolved: '解決済み',
      falsePositive: '誤検知',
      pendingApproval: '承認待ち',
      escalated: 'エスカレート済み',
      approved: '承認済み',
      underObservation: '観察中',
      device: 'デバイス',
      incident: 'インシデント',
      activeSince: 'チケット作成日時',
      status: 'ステータス',
      aiPlan: 'AIプラン',
      actions: 'アクション',
      viewPlan: 'プランを見る',
      aiTroubleshootingPlan: 'AIトラブルシューティングプラン',
      sendReport: 'レポートを送信',
      generateWorkflow: 'ワークフローを生成',
      executing: '実行中...',
      noActiveIncidents: 'アクティブインシデントは見つかりませんでした。',
      triggeringDemoAlerts: 'デモアラートを起動中...',
      demoAlertsTriggered: 'デモアラートを起動しました！インシデントを更新中...',
      failedToTriggerDemo: 'デモアラートの起動に失敗しました。',
      statusUpdated: 'インシデントステータスを更新しました：',
      failedToUpdateStatus: 'ステータスの更新に失敗しました。',
      pleaseSelectIncident: '履歴を表示するには少なくとも1つのインシデントを選択してください',
      resolutionEmailSent: '解決レポートのメールを送信中！',
      failedToSendEmail: '解決レポートのメール送信に失敗しました。',
      workflowCompleted: 'ワークフローが正常に完了しました！',
      workflowFailed: 'ワークフローの実行に失敗しました',
      goTo: 'ページへ：',
      failedToFetchAlerts: 'アラートの取得に失敗しました。',
      priorityColumnHeader: '優先度',
      priorityCritical: '重大',
      priorityWarning: '警告',
      statusFilters: {
        all: '全て',
        new: '新規',
        inProgress: '進行中',
        resolved: '解決済み',
        escalated: 'エスカレート済み',
        underObservation: '観察中',
      },
      timeSince: {
        yearsAgo: '年前',
        monthsAgo: 'ヶ月前',
        daysAgo: '日前',
        hoursAgo: '時間前',
        minutesAgo: '分前',
        secondsAgo: '秒前',
      },
      issueDescription: {
        tabLabel: '問題の説明',
        aiPlanTab: 'AIプラン',
        whatHappened: '何が起きたか',
        whenItHappened: '発生日時',
        siteAffected: '影響を受けたサイト',
        clientsAffected: '影響クライアント数',
        impact: '影響',
        affectedIp: '影響IPアドレス',
        threatSummary: '脅威サマリー',
        raisedBy: '報告者',
        noDescription: '詳細な問題説明はありません。',
        noDescriptionSub: 'このインシデントはメール経由で報告されていないか、説明データが抽出されていません。',
        // Meraki-specific
        organization: '組織',
        network: 'ネットワーク',
        deviceModel: 'デバイスモデル',
        deviceMac: 'MACアドレス',
        deviceTags: 'デバイスタグ',
        alertDetails: 'アラート詳細',
        portNumber: 'ポート番号',
        portDescription: 'ポート説明',
        portStatus: 'ポートステータス',
        previousStatus: '以前のステータス',
        malwareHash: 'マルウェアハッシュ (SHA256)',
        powerSupplyNum: '電源ユニット番号',
        viewInMeraki: 'Merakiで表示',
      },
    },

    // AI Action Modal
    aiModal: {
      initiateAction: 'アクションを開始：',
      sendReport: 'レポートを送信',
      sendingReport: 'レポートを送信中...',
      workflow: 'ワークフロー',
      impactAssessment: '影響評価',
      resolution: '解決',
      incidentDetails: 'インシデント詳細',
      approvalRequired: '承認が必要',
      approvalRequiredDesc: 'このワークフローを確認・カスタマイズした後、封じ込め承認者による承認のために提出できます。直接ワークフローを実行することはできません。',
      aiRecommendedWorkflow: 'AI推奨ワークフロー',
      aiRecommendedDescExecute: 'ポリシーが見つかりませんでした。AIは以下のプランを提案します。実行前にカスタマイズできます。',
      aiRecommendedDescSubmit: 'ポリシーが見つかりませんでした。AIは以下のプランを提案します。提出前にカスタマイズできます。',
      exactPolicyMatch: '正確なポリシーが一致しました',
      executing: '実行中',
      submitting: '提出中',
      standardProcedure: '標準手順：',
      similarPolicyMatch: '類似ポリシーが一致しました',
      policy: 'ポリシー：',
      confidence: '信頼度：',
      matchType: 'マッチタイプ：',
      workflowBuilder: 'ワークフロービルダー',
      aiDeviceActions: 'AIデバイス推奨アクション',
      aiDeviceActionsDesc: 'アラートタイプとデバイスに基づいて、AIは以下の設定変更を提案します。これらはMerakiデバイスに直接適用されます。',
      risk: 'リスク',
      applying: '適用中...',
      applyNow: '今すぐ適用',
      deviceWarning: 'これらのアクションはライブMerakiデバイスの設定を変更します。適用前に慎重にご確認ください。',
      actionDetails: 'アクション詳細',
      actionParam: 'アクション',
      editableParams: '編集可能なパラメーター',
      required: '必須',
      justification: '正当化理由 *',
      justificationPlaceholder: 'このワークフローを実行する理由を説明してください...',
      justificationHintApprover: '監査証跡および文書化のために必要です。',
      justificationHintReviewer: '実行前に承認者によってレビューされます。',
      confirmExecute: '直接実行を確認するには「EXECUTE」と入力してください：',
      saveToAiPolicies: 'AIポリシーに保存',
      submitForPeerReview: 'ピアレビューに提出',
      executeNow: '今すぐ実行',
      submitForReview: 'レビューに提出',
      executingAction: '実行中...',
      couldNotLoad: 'アクションプランを読み込めませんでした',
      savingPolicy: 'ポリシーを保存中...',
      initiatingAction: 'アクションを開始中...',
      submittingForReview: 'レビューのためワークフローを提出中...',
      impactTitle: '影響評価',
      currentImpactLevel: '現在の影響レベル',
      estimatedUsersAffected: '影響を受けるユーザーの推定数',
      impactReason: '影響の理由：',
      impactManualOverride: '影響レベルが手動で上書きされました',
      updateImpactLevel: '影響レベルを更新',
      overrideDesc: 'インシデントの影響について追加のコンテキストがある場合は、自動評価を上書きしてください。',
      high: '高',
      medium: '中',
      low: '低',
      impactUnavailable: '影響データは利用できません',
      rootCause: '根本原因 / 問題分析',
      stepsTaken: '解決のために取られたステップ',
      noStepsRecorded: 'このインシデントに対して自動化されたステップは記録されませんでした。',
      recurrencePrevention: '再発防止',
      sendResolutionReport: '解決レポートを送信',
      sendingResolutionReport: 'レポートを送信中...',
      resolutionUnavailable: '解決データは利用できません',
      submittedForPeerReview: 'ワークフローがピアレビューのために提出されました！承認をお待ちください。',
      submittedSuccessfully: 'ワークフローが正常に提出されました！',
      actionInitiated: 'アクションを開始しました！実行ID：',
      resolutionReportSent: '解決レポートが正常に送信されました',
      policySaved: 'ポリシーが正常に保存されました！',
      policyNameRequired: 'ポリシー名は必須です',
      enterPolicyName: 'このポリシーの名前を入力してください：',
      justificationRequired: 'このアクションの正当化理由を入力してください',
      impactLevelUpdated: '影響レベルを更新しました：',
      stepAdded: 'ワークフローにステップを追加しました。',
      stepRemoved: 'ワークフローからステップを削除しました。',
      unknown: '不明な時間',
      by: '担当者：',
      // Workflow status banner
      workflowExecuting: 'ワークフローを実行中...',
      workflowCompletedSuccess: 'ワークフローが正常に完了しました',
      workflowExecutionFailed: 'ワークフローの実行に失敗しました',
      // HITL approval phase
      workflowPaused: 'ワークフロー一時停止 — 人間の承認が必要',
      workflowPausedDesc: '以下のアクションを確認し、続行するには承認または拒否してください。',
      whatHappenedSoFar: 'これまでの経緯',
      stepsCompleted: 'ステップ完了',
      pendingAction: '保留中のアクション',
      parametersLabel: 'パラメーター：',
      approveAndContinue: '承認して続行',
      rejectAndStop: '拒否して停止',
      shareForApprovalEmail: 'メールで承認を依頼',
      approvalEmailSent: '承認リクエストをメールで送信しました',
      // Pending review phase
      submittedForPeerReviewTitle: 'ピアレビューに提出しました',
      submittedForPeerReviewDesc: '承認者がこのワークフローを確認・実行します。',
      // Execution history
      executionHistory: '実行履歴',
      executionLabel: '実行',
      waitingForSteps: 'ステップを待機中...',
      statusCompleted: '完了',
      statusFailed: '失敗',
      statusRunning: '実行中...',
      statusAwaitingApproval: '承認待ち',
      // Save policy panel
      saveAsAiPolicy: 'AIポリシーとして保存',
      policyName: 'ポリシー名',
      generatingTitle: 'AIタイトルを生成中...',
      enterPolicyNamePlaceholder: 'ポリシー名を入力',
      policyDescription: '説明',
      policyDescriptionPlaceholder: 'このポリシーの内容を簡単に説明してください',
      templateMode: 'テンプレートモード：',
      templateModeDesc: '実際の値はプレースホルダーに置き換えられます：',
      savePolicy: 'ポリシーを保存',
      // Impact level labels
      impactLevelUnknown: '不明',
    },

    // Event Action History Modal
    historyModal: {
      actionHistoryFor: 'のアクション履歴：',
      description: 'このイベントに対応したすべてのワークフロー実行の時系列ログです。',
      noExecutions: 'このインシデントに対してワークフロー実行はまだ記録されていません。',
      failedToLoad: 'ワークフロー実行履歴の読み込みに失敗しました。',
      incident: 'インシデント #',
      completed: '完了',
      failed: '失敗',
      steps: 'ステップ',
      success: '成功',
      target: '対象：',
      viewDetails: '詳細を表示',
      parameters: 'パラメーター：',
      result: '結果：',
    },

    // Settings page
    settings: {
      knowledgeManagement: 'ナレッジ管理',
      knowledgeManagementDesc: 'AIナレッジベース用のドキュメントをアップロード・管理する',
      alertNotifications: 'アラート通知',
      alertNotificationsDesc: '自動メールアラートと受信者を設定する',
      resourceMonitoring: 'リソース監視',
      resourceMonitoringDesc: 'CPU、メモリ、セッション、トラフィックのメールアラート閾値を設定する',
      aiPolicies: 'AIポリシー',
      aiPoliciesDesc: '自然言語を使用して自動インシデント対応ワークフローを作成する',
      customTools: 'カスタムツール',
      customToolsDesc: 'チャットボット用のカスタムAPIツールを作成する',
      securitySettings: 'セキュリティ設定',
      securitySettingsDesc: '認証、APIキー、アクセス制御を管理する',
      integrations: 'インテグレーション',
      integrationsDesc: 'NOCアラートおよびSOCコマンドリスニング用のOutlookメールアカウントを接続する',
      comingSoon: '近日公開',
      proTip: 'AIノックマネージャーをネットワーク環境と運用ニーズに最適化するために設定を構成してください。',
      proTipLabel: 'プロのヒント：',
    },

    // Live Network page
    liveNetwork: {
      devices: 'デバイス',
      recentEvents: '最近のイベント',
      eventHistory: 'イベント履歴',
      correlation: '相関分析',
      totalEvents: '総イベント数',
      eventBursts: 'イベントバースト',
      problemDevices: '問題のあるデバイス',
      potentialRootCauses: '潜在的な根本原因',
      commonPatterns: '共通パターン',
      recentEventsCount: '最近のイベント',
      eventHistoryWithAI: 'AIアナリシス付きイベント履歴',
      noRecentEvents: '最近のイベントはありません',
      noHistoricalEvents: '履歴イベントはありません',
      failedToLoad: 'ネットワーク詳細の読み込みに失敗しました。もう一度お試しください。',
      selectNetwork: 'サイドバーからネットワークを選択して詳細を表示してください。',
      aiAnalysis: 'AI分析：',
      device: 'デバイス：',
    },

    // Workbench page
    workbench: {
      aiWorkbench: 'AIワークベンチ',
      containmentProposals: '封じ込め提案',
      approveAll: 'すべて承認',
      denyAll: 'すべて拒否',
      approve: '承認',
      deny: '拒否',
      activeContainments: 'アクティブな封じ込め',
      auditLogs: '監査ログ',
      noProposals: '提案はありません',
      noContainments: '封じ込めはありません',
      noLogs: 'ログはありません',
      action: 'アクション',
      target: '対象',
      timestamp: 'タイムスタンプ',
      user: 'ユーザー',
      result: '結果',
    },

    // Security Events page
    securityEvents: {
      title: 'AI防衛ログ',
      noEvents: 'セキュリティイベントはありません',
      eventDetails: 'イベント詳細',
      violations: '違反',
      messages: 'メッセージ',
      copyId: 'IDをコピー',
      copied: 'コピーしました！',
    },

    // Manager page
    manager: {
      title: 'AI NOCマネージャー',
    },

    // NOC Agent Status Card (Circuit Breaker)
    nocStatus: {
      nocAgent: 'NOCエージェント',
      circuitBroken: '回路切断',
      circuitBreakResolved: '回路切断 解決済み',
      sessionRestored: 'セッション復元 — サーキットブレーカー解除',
      operational: '正常稼働 — CBトンネル有効',
      liveSessionInactive: 'ライブセッション非アクティブ — 再接続中…',
      isolatedBySOC: 'SOCコマンドにより隔離済み',
      triggeredBy: '実行者：',
      triggeredAt: '実行日時：',
      publicIp: 'パブリックIP：',
      markAsResolved: '解決済みとしてマーク',
      appsLabel: 'アプリ',
      hideBeforeAfter: '前後を非表示',
      showBeforeAfter: '前後を表示',
      before: '前',
      after: '後',
      active: 'アクティブ',
      terminated: '終了',
      toastResolvedTitle: 'サーキットブレーカー解決済み',
      toastResolvedDesc: 'NOCエージェントの再接続を確認 — 監査記録が閉じられました。',
      toastAlreadyResolvedTitle: '解決済み',
      toastAlreadyResolvedDesc: 'アクティブなサーキットブレーカーは見つかりませんでした。',
    },

    // SOC Isolation Page
    nocIsolation: {
      pageTitle: 'NOCエージェント隔離',
      pageDesc: 'NOC AIエージェントのサーキットブレーカー制御',
      refresh: '更新',
      deviceMac: 'デバイスMAC',
      isolatedAt: '隔離日時',
      triggeredBy: '実行者',
      lastSeen: '最終確認',
      triggeringEmail: '発動メール',
      resources: 'リソース',
      blocked: 'ブロック',
      ok: 'OK',
      resourceUnavailable: 'リソースステータス利用不可',
      manualCircuitBreak: '手動サーキットブレーク',
      reasonLabel: '理由 *',
      reasonPlaceholder: '例：異常なトラフィックスパイク、手動操作の決定...',
      incidentRefLabel: 'インシデント参照（任意）',
      incidentRefPlaceholder: '例：INC-12345',
      isolateBtn: 'NOCエージェントを隔離',
      isolationTimeline: '隔離タイムライン',
      noEventsYet: 'まだ隔離イベントはありません',
      circuitBroken: 'サーキット切断',
      restored: '復元',
      timestamp: 'タイムスタンプ',
      cbResponse: 'CBレスポンス',
      emailExcerpt: 'メール抜粋',
      appsBeforeAfter: 'アプリ（前後）',
      hideApps: 'アプリを非表示',
      showApps: 'アプリを表示',
      appName: 'アプリ名',
      before: '前',
      after: '後',
      active: 'アクティブ',
      terminated: '終了',
      accessible: 'アクセス可',
      circuitOperational: '正常稼働',
      loading: '読み込み中…',
      loadingHistory: '履歴を読み込み中…',
      toastIsolated: 'NOCエージェントが隔離されました',
      toastValidation: '隔離の理由を入力してください',
      toastIsolationFailed: '隔離に失敗しました',
      justNow: 'たった今',
      minutesAgo: '分前',
      hoursAgo: '時間前',
      daysAgo: '日前',
      nocAgentLabel: 'NOCエージェント',
      appsCount: 'アプリ',
      triggerSource: {
        EMAIL_COMMAND: 'メールコマンド',
        DSPA_SCRIPT: 'DSPAスクリプト',
        MANUAL_UI: '手動UI',
        MANUAL_VERIFY: '手動確認',
        DSPA_TRAFFIC_SPIKE: 'DSPAトラフィックスパイク',
        DSPA_AUTO: 'DSPA自動',
      },
    },

    // Alerts page — tab labels & SOC circuit break card
    alertsPage: {
      nocAlertsTab: 'NOCアラート',
      socAlertsTab: 'SOCアラート',
      loadingSocAlerts: 'SOCアラートを読み込み中…',
      noSocAlerts: 'SOCサーキットブレークコマンドは受信されていません',
      noSocAlertsDesc: '「サーキットブレーク」を含むメールをアナリストが送信すると、コマンドが届きます',
      circuitRestored: 'サーキット復元',
      circuitBroken: 'サーキット切断',
      triggerEmail: 'メールコマンド',
      triggerBandwidth: '帯域幅スパイク',
      triggerManual: '手動',
      receivedFrom: '受信元：',
      workflow: 'ワークフロー',
      stepEmailReceived: 'メール受信',
      stepCircuitBreakInitiated: 'サーキットブレーク開始',
      stepRestored: '復元',
      markAsResolved: '解決済みとしてマーク',
      markAsResolvedDesc: 'NOCエージェントが再接続されたことを確認し、この監査記録を閉じるにはクリックしてください。',
      resolvedToast: 'サーキットブレーク解決済み — NOCエージェントセッションの再接続を確認しました',
      resolveFailToast: 'サーキットブレークの解決に失敗しました — もう一度お試しください',
      hideApps: 'アプリを非表示',
      viewApps: 'アプリを表示（{count}件影響）',
      appsTotal: 'アプリ（合計{count}件）',
      appsLiveBadge: 'ライブ',
      appsRefresh: '更新',
      appsFetchingLive: 'CloudBrinkから取得中…',
      appsLiveError: '— 保存済みスナップショットを表示',
      appsColName: '名前',
      appsColBefore: '前',
      appsColAfter: '後',
      appsStatusActive: 'アクティブ',
      appsStatusTerminated: '終了',
      appsPublicIp: 'NOC パブリックIP：',
      appsSnapshot: 'スナップショット：',
      justNow: 'たった今',
      minutesAgo: '分前',
      hoursAgo: '時間前',
      daysAgo: '日前',
    },

    // Ask NOC Manager / Chatbot
    askNoc: {
      welcomeTitle: 'AI NOCマネージャーへようこそ！ 👋',
      welcomeText: 'ネットワーク運用をサポートするインテリジェントアシスタントです。',
      placeholder: '質問を入力してください...',
      placeholderFull: 'ここに質問を入力してください...',
      addQuestion: '質問を追加...',
      fetchingData: 'データを取得中...',
      errorMessage: 'AIへの接続で問題が発生しました。',
      suggestedQuestions: [
        'ネットワーク一覧を表示',
        'ネットワークスパイクを確認',
        '最近の重大アラートとトラブルシューティング方法は？',
        'ネットワーク健全性の概要を表示',
        '信号強度が弱いAPを特定しレポートを作成',
      ],
      // Spike test
      spikeConnecting: '接続中…',
      spikeDownloading: 'ダウンロード中…',
      spikeDownloadComplete: '✓ ダウンロード完了',
      spikeDownloadFailed: '✗ ダウンロード失敗',
      spikeMbDownloaded: 'MB ダウンロード済み',
      spikeMbRemaining: 'MB 残り',
      spikeStatsFormat: '{elapsed}s / 60s',
      spikeConnectionInterrupted: 'ダウンロード失敗 — 接続が切断されました。',
      spikeDownloadCompletedMsg: 'ダウンロードが完了しました。',
      spikeSessionTerminatedCallout: 'ダウンロードに失敗しました。サーキットブレーカーによりNOCセッションが終了したため、ファイルを完了できませんでした。',
      spikeDownloadingForTest: 'ネットワークスパイクテスト用のファイルをダウンロード中…',
    },

    // Common
    common: {
      loading: '読み込み中...',
      error: 'エラー',
      retry: '再試行',
      close: '閉じる',
      save: '保存',
      cancel: 'キャンセル',
      confirm: '確認',
      delete: '削除',
      edit: '編集',
      add: '追加',
      search: '検索',
      filter: 'フィルター',
      export: 'エクスポート',
      import: 'インポート',
      refresh: '更新',
      back: '戻る',
      next: '次へ',
      previous: '前へ',
      submit: '送信',
      reset: 'リセット',
      unknown: '不明',
      unknownDevice: '不明なデバイス',
      unknownIncident: '不明なインシデント',
      na: 'N/A',
    },

    // KPI / dashboard widgets
    kpi: {
      highPriority: '高優先度',
      mediumPriority: '中優先度',
      resolved: '解決済み',
      filterByNetwork: 'ネットワークでフィルター',
      allNetworks: '全てのネットワーク',
      lockFilter: 'フィルターをロック',
      unlockFilter: 'フィルターのロックを解除',
      setHighPriority: '高優先度に設定',
      setMediumPriority: '中優先度に設定',
      breakdownByNetwork: 'ネットワーク別内訳',
      sourceAll: 'すべて',
      sourceNoc: 'NOC',
      sourceSoc: 'SOC',
      summaryLabel: 'アラート状況',
      summaryStatusActive: 'アクティブ',
      summaryStatusAllClear: 'クリア',
      summaryStatusNoAlerts: '異常なし',
      summaryAllClear: 'すべてクリア！{resolved}件のインシデントが解決しました。',
      summaryNoAlerts: '現在アクティブなアラートはありません。',
      summaryHighPrefix: '高優先度 {count}件',
      summaryMediumPrefix: '中優先度 {count}件',
      summaryResolvedSuffix: '解決済み {count}件',
      summaryAnd: 'および',
      summaryAffectedNetwork: '{network} に集中',
      summaryAlertTypes: 'アラート種別:',
    },

    // Network health
    networkHealth: {
      title: 'ネットワーク健全性スコア',
      online: 'オンライン',
      alerting: 'アラート中',
      offline: 'オフライン',
      dormant: '休止中',
    },

    // Organization Metrics widget
    organizationMetrics: {
      sectionTitle: 'ネットワーク稼働率 & アクティブクライアント',
      allNetworks: '全てのネットワーク',
      networkUptime: 'ネットワーク稼働率',
      totalActiveClients: '総アクティブクライアント数',
      totalActiveClientsScope: '（有線 + 無線）',
      loading: '読み込み中…',
      devicesOnline: '台がオンライン（全',
      devicesOnlineSuffix: '台中）',
      across: '約',
      networks: 'ネットワーク',
    },

    // Resource Monitoring widget badge labels
    resourceMonitoringLabels: {
      subtitle: 'AP（無線クライアントのみ） · スイッチ · CPU · メモリ',
      clientsThisNetwork: 'AP無線クライアント（このネットワーク）',
      allNetworks: 'AP無線クライアント（全ネットワーク）',
      wirelessOnly: '※無線APクライアントのみ（有線除く）',
    },

    // Client Top Talkers widget
    clientTopTalkers: {
      title: 'クライアントトップトーカー',
      subtitle: '— クライアント別帯域幅（リアルタイム）',
      sent: '送信',
      received: '受信',
      loading: 'クライアント使用量を読み込み中…',
      noData: 'クライアント使用データがありません',
      noDataSub: 'アクティブなクライアントが見つかりませんでした',
      totalClients: '合計クライアント数',
      aboveAverage: '× 平均使用量を超過',
      os: 'OS',
      ssid: 'SSID',
      network: 'ネットワーク',
    },

    // Device Fleet widget
    deviceFleet: {
      title: 'デバイスフリート',
      subtitle: '— 製品ファミリー別ライブステータス',
      online: 'オンライン',
      dormant: '休止中',
      alerting: 'アラート中',
      offline: 'オフライン',
      totalDevices: '台合計',
      loading: 'デバイスフリートを読み込み中…',
      noData: 'デバイスデータがありません',
      total: '合計',
    },

    // Network Overview Cards (Live Network page)
    networkOverviewCards: {
      aiHealthScore: 'AIヘルススコア',
      keyInsights: 'キーインサイト：',
      activeClientsConnected: '台のアクティブクライアントが接続中',
      refreshing: '更新中...',
      refresh: '更新',
      networkOverview: 'ネットワーク概要',
      networkId: 'ネットワークID：',
      timeZone: 'タイムゾーン：',
      activeClients: 'アクティブクライアント：',
      productTypes: '製品タイプ：',
      orgId: '組織ID：',
      trafficSummary: 'トラフィックサマリー（過去1時間）',
      sent: '送信：',
      received: '受信：',
      total: '合計：',
      healthy: '正常',
      warning: '警告',
      critical: '危機的',
      failedToRefresh: 'ヘルスサマリーの更新に失敗しました。再試行してください。',
    },

    // Network Context Selector
    networkContextPanel: {
      title: 'ネットワークコンテキスト',
      subtitle: '監視するネットワークを選択',
      searchPlaceholder: '検索',
      noNetworksFound: 'ネットワークが見つかりません',
    },

    // Devices Status Table
    devicesStatus: {
      title: 'デバイスステータス',
      excluded: '除外済み',
      device: 'デバイス',
      name: '名前',
      model: 'モデル',
      serial: 'シリアル',
      status: 'ステータス',
      actions: 'アクション',
      noDevices: 'デバイスが見つかりません',
      restoreMonitoring: '監視を再開',
      markAsExcluded: '除外としてマーク',
      excludeTitle: 'デバイスを除外としてマーク',
      excludeDesc: 'このデバイスは障害検出とAIヘルススコアから除外されます。「除外済み」ステータスでネットワークビューに表示されたままになります。',
      reasonLabel: '理由（任意）',
      reasonPlaceholder: '例：定期メンテナンス、廃止、ラボデバイス…',
      confirmExclusion: '除外を確認',
      saving: '保存中…',
      restoreTitle: 'デバイスを監視に復元',
      restoreDesc: 'このデバイスは除外リストから削除され、障害検出とAIヘルススコアに再び含まれます。',
      restoring: '復元中…',
      exclusionDetails: '除外詳細',
      serialLabel: 'シリアル：',
      reasonDetailLabel: '理由：',
      excludedByLabel: '除外者：',
      excludedAtLabel: '除外日時：',
      viewExclusionDetails: '除外詳細を表示',
    },

    // Workbench page (extended)
    workbenchPage: {
      analyzing: 'AIストラテジーエージェントが分析中...',
      proposeContainment: '封じ込めを提案',
      network: 'ネットワーク：',
      aiRecommendedStrategy: 'AI推奨封じ込め戦略',
      aiRecommendation: 'AI推奨',
      reason: '理由：',
      selectStrategy: '封じ込め戦略を選択',
      isolateAllClients: '全クライアントを隔離',
      isolateAllClientsDesc: 'このネットワーク上の全クライアントトラフィックをブロック',
      isolateSwitchPort: '特定のスイッチポートを隔離',
      isolateSwitchPortDesc: '単一の有線デバイスポートを隔離',
      isolateWirelessSSID: '無線SSIDを隔離',
      isolateWirelessSSIDDesc: '特定の無線ネットワークを隔離',
      isolateVLAN: 'VLAN全体を隔離',
      isolateVLANDesc: '広範なインシデントの最終手段',
      justification: '正当化理由（AIが事前入力）',
      justificationPlaceholder: 'この封じ込めが必要な理由を説明してください',
      adminDirectExecution: '管理者による直接実行',
      adminDirectExecutionDesc: 'すぐに封じ込めを実行しようとしています。',
      typeIsolate: "実行を確認するには「ISOLATE」と入力してください",
      executeNow: '今すぐ実行',
      submitForReview: 'レビューに提出',
      cancel: 'キャンセル',
      currentlyIsolatedNetworks: '現在隔離中のネットワーク',
      noNetworksIsolated: '現在隔離されているネットワークはありません。',
      pendingApprovals: '承認待ち',
      pendingApprovalsDesc: 'チームからのワークフロー実行および封じ込めリクエストを確認・承認してください。',
      noPendingProposals: '承認待ちの提案はありません。',
      approvedWorkflows: '承認済みワークフロー - 実行準備完了',
      approvedWorkflowsDesc: 'これらのワークフローは承認済みで実行可能です。',
      noApprovedWorkflows: '実行待ちの承認済みワークフローはありません。',
      auditTrail: '封じ込め監査証跡',
      auditTrailDesc: '全封じ込めアクションの完全な履歴。',
      noAuditTrail: '監査証跡はありません。',
      auditTrailNote: '封じ込めアクションが実行されると、監査ログがここに表示されます。',
      timestamp: 'タイムスタンプ',
      action: 'アクション',
      networkCol: 'ネットワーク',
      details: '詳細',
      proposedBy: '提案者：',
      reviewedBy: 'レビュー者：',
      comment: 'コメント：',
      checkStatus: 'ステータス確認',
      release: '解除',
      containedBy: '封じ込め者：',
      proposedByOn: '提案者 ',
      on: ' — ',
      approvedBadge: '承認済み',
      youCanExecute: 'このワークフローを実行できます',
      onlyCanExecute: 'のみが実行可能',
      executing: '実行中...',
      execute: '実行',
      approveContainment: '封じ込め提案を承認',
      denyContainment: '封じ込め提案を拒否',
      reviewComment: 'レビューコメント（必須）',
      reviewPlaceholder: 'この提案を承認/拒否する理由を入力してください...',
      confirmApprove: '承認を確認',
      confirmDeny: '拒否を確認',
      noPermission: 'このページを閲覧する権限がありません。',
      noPermissionSection: 'このセクションを閲覧する権限がありません。',
      noPermissionAudit: '監査証跡を閲覧するには「Containment Initiator」または「Containment Approver」の役割が必要です。',
      tabActive: 'アクティブアクション',
      tabPending: '承認待ち',
      tabApproved: '実行準備完了',
      tabAudit: '監査証跡',
      submittingRequest: '封じ込めリクエストを提出中...',
      requestSubmitted: '封じ込めリクエストをレビューに提出しました！',
      executingContainment: '封じ込めを実行中...',
      executionSuccess: '封じ込めが正常に実行されました！',
      releasingNetwork: 'ネットワークを解除中...',
      releaseSuccess: 'ネットワークの解除を開始しました。',
      executingWorkflow: 'ワークフローを実行中...',
      confirmRelease: 'ネットワーク「{name}」を解除してよろしいですか？',
      confirmExecuteWorkflow: 'このワークフローを実行してよろしいですか？',
      testing: 'テスト中',
    },

    // Knowledge Management page
    knowledge: {
      title: 'ナレッジ管理',
      subtitle: 'AIナレッジベース用のドキュメントをアップロード・管理する',
      back: '戻る',
      uploadDocument: 'ドキュメントをアップロード',
      uploadedDocuments: 'アップロード済みドキュメント',
      clickToUpload: 'クリックしてアップロード',
      orDragAndDrop: 'またはドラッグ＆ドロップ',
      nativeFormats: 'PDF、DOCX、PPTX、XLSX、MP3、MP4、WAV、EML、MSG（ネイティブ）',
      autoConvertFormats: 'PNG、JPG、GIF、JSON、TXT、CSV、HTML、MDなど — アップロード時に自動変換',
      maxSize: '最大64 MB',
      uploading: 'アップロード中...',
      documentName: 'ドキュメント名',
      status: 'ステータス',
      actions: 'アクション',
      statusReady: '準備完了',
      statusProcessing: '処理中',
      statusFailed: '失敗',
      noDocuments: 'まだドキュメントがアップロードされていません',
      noDocumentsSub: '最初のドキュメントをアップロードしてナレッジベースを構築してください',
      deleteConfirm: '「{name}」を削除してよろしいですか？',
      uploadSuccess: '「{name}」を正常にアップロードしました',
      uploadSuccessNote: '「{name}」を正常にアップロードしました。注意：',
      deleteSuccess: '「{name}」を正常に削除しました',
      fileTooLarge: 'ファイルサイズは64 MB未満である必要があります',
      cannotDeleteProcessing: '処理中は削除できません',
      deleteDocument: 'ドキュメントを削除',
    },

    // Integrations page
    integrations: {
      back: '戻る',
      title: 'インテグレーション',
      subtitle: 'NOCアラート通知とSOCコマンドリスニングを有効にするためにOutlookメールアカウントを接続してください。',
      checking: '確認中…',
      connected: '接続済み',
      notConnected: '未接続',
      loadingStatus: 'ステータスを読み込み中…',
      tokenValid: 'トークン有効',
      autoRefreshes: '自動更新',
      tokenExpiredFor: 'のトークンが期限切れ — 再接続して復元してください',
      noAccountConnected: 'Outlookアカウントが接続されていません。「接続」をクリックしてMicrosoft OAuthで認証してください。',
      listeningFolder: 'リスニングフォルダー',
      subfolderInMailbox: 'の接続済みメールボックスのサブフォルダー',
      outboundNotifications: 'アウトバウンド通知',
      outboundDesc: 'アラートメールは接続済みメールボックスからアラート設定の受信者に送信されます',
      inboundBehaviour: 'インバウンド動作',
      inboundNoc: '受信メール → ネットワークイベントおよび封じ込め提案として解析',
      inboundSoc: '受信メール → サーキットブレーク、停止、または解除コマンドとして解析',
      reconnectAccount: 'アカウントを再接続',
      connectAccount: 'アカウントを接続',
      refreshStatus: 'ステータスを更新',
      socDisabledReason: 'SOC Outlookインテグレーションを管理するにはSOCアナリストの役割が必要です',
      footerNote: '注意：',
      footerText: 'NOCとSOCは同じ物理メールボックスを使用します。フォルダー名が各受信メールの処理プロセスを制御します。',
      footerNocFolder: 'NOCフォルダーのメールはネットワークインシデントとして処理され、',
      footerSocFolder: 'SOCフォルダーのメールはアナリストコマンドとして解析されます。',
      outlookConnectedSuccess: 'Outlookが正常に接続されました',
      outlookConnectionFailed: 'Outlookの接続に失敗しました',
      remaining: '残り',
    },

    // SOC Alerts page
    socAlerts: {
      title: 'アラート',
      subtitle: 'NOCネットワークイベントとSOCサーキットブレークコマンド',
      refresh: '更新',
      tabNocMeraki: 'NOC — Meraki',
      tabNocEmail: 'NOC — メール',
      tabSocCircuitBreak: 'SOC — サーキットブレーク',
      tabSocSuspensions: 'SOC — 停止',
      activeSuspension: 'アクティブ停止',
      activeSuspensions: 'アクティブ停止',
      merakiWebhookAlerts: 'Merakiウェブフックアラート',
      emailSourcedAlerts: 'メールソースNOCアラート',
      socCircuitBreakAll: 'SOCサーキットブレーク — 全イベント',
      symphonyTrustSuspensions: 'Symphony Trust — NOC停止イベント',
      noMerakiAlerts: 'Merakiウェブフックアラートはありません',
      noEmailAlerts: 'メールソースアラートはありません',
      noCircuitBreakEvents: 'サーキットブレークイベントは記録されていません',
      noSuspensionEvents: '停止イベントは記録されていません',
      aiPlan: 'AIプラン：',
      by: '実行者：',
      viaEmailCommand: 'メールコマンド経由',
      fromSocDashboard: 'SOCダッシュボードから',
      agentsSuspended: 'エージェント停止',
      agentSuspended: 'エージェント停止',
      aiEmployeeSuspended: 'AI従業員停止',
      fullOperationDetails: '全操作詳細',
      resumedBy: '再開者：',
      resumeRestoreAgents: 'エージェントを再開・復元',
      resumingAgents: 'エージェントを再開中・資格情報を再発行中…',
      verifyingAgents: 'エージェントステータス・資格情報を確認中…',
      restored: '復元済み',
      circuitBroken: 'サーキット切断',
      triggerSourceEmail: 'メール',
      triggerSourceManual: '手動',
      triggerSourceDspa: 'DSPA',
      triggerSourceVerify: '確認',
      triggerSourceOutlook: 'Outlook',
      triggerSourceCiscoAI: 'Cisco AI Defense',
      fromCiscoAiDefense: 'Cisco AI Defenseが検出',
      cbSourceEmail: 'メールコマンド',
      cbSourceOutlook: 'Outlookメール',
      cbSourceManual: '手動',
      cbSourceDspa: 'DSPAアラート',
      cbSourceVerify: 'セッション確認',
      suspended: '停止中',
      resumed: '再開済み',
      scope: 'スコープ：',
      triggeredBy: '実行者：',
      suspensionEventTitle: '停止イベント #',
      fullOpLog: '全影響エンティティの完全な操作ログ',
      noEntityDetails: 'エンティティ詳細が記録されていません',
      resumeRestoreAll: '全エージェントを再開・復元',
      resuming: '再開中…',
      verifying: '確認中…',
      accessRestored: 'アクセスが復元されました',
      resumedWithWarnings: '警告付きで再開されました',
      resumeFailed: '再開に失敗しました',
      allAgentsRestored: '全エージェントが復元・確認されました',
      someAgentsNeedAttention: '再開済み — 一部エージェントが注意を要します',
      justNow: 'たった今',
      minutesAgo: '分前',
      hoursAgo: '時間前',
      daysAgo: '日前',
      loading: '読み込み中…',
    },

    // Cisco AI Defense page
    ciscoDefense: {
      title: 'Cisco AI Defense ランタイムイベント',
      subtitle: 'Cisco AI Defenseゲートウェイが追跡するセキュリティイベント · 行をクリックしてブロックされたコンテンツを表示',
      live: 'ライブ · 30秒',
      total: '合計：',
      page: 'ページ：',
      filtered: 'フィルター：',
      allActions: '全アクション',
      block: 'ブロック',
      allow: '許可',
      reset: 'リセット',
      exportCsv: 'CSVエクスポート',
      exportSuccess: '正常にエクスポートされました',
      exportFailed: 'エクスポートに失敗しました',
      colDateTime: '日時',
      colAction: 'アクション',
      colApplicationId: 'アプリケーションID',
      colPolicyId: 'ポリシーID',
      colEventId: 'イベントID',
      failedToFetch: 'イベントの取得に失敗しました',
      retry: '再試行',
      noEventsFound: 'イベントが見つかりません。',
      showing: '表示中',
      of: '/',
      previous: '前へ',
      next: '次へ',
      copyFullId: '全IDをコピー',
      copied: 'コピーしました！',
      eventDetail: 'イベント詳細',
      couldNotLoadDetail: 'イベント詳細を読み込めませんでした。',
      failedToLoadDetail: 'イベント詳細の読み込みに失敗しました',
      eventInfo: 'イベント情報',
      date: '日時',
      action: 'アクション',
      eventId: 'イベントID',
      applicationId: 'アプリケーションID',
      policyId: 'ポリシーID',
      connectionId: 'コネクションID',
      whyBlocked: 'ブロックされた理由',
      allChecksRun: '実行された全チェック',
      blockedContent: 'ブロックされたコンテンツ',
      noContentAvailable: '(コンテンツなし)',
    },

    // Device Power Monitoring settings page (ja)
    deviceMonitoring: {
      title: 'デバイス電源監視',
      subtitle: '電源オフイベントを監視する組織とデバイスを設定します。監視中のデバイスがオフラインになるとアラートが発生し、復帰すると自動的に解決されます。',
      back: '戻る',
      notAuthenticated: '未認証',
      notAuthenticatedDesc: 'デバイス監視を設定するにはサインインしてください。',
      goToDashboard: 'ダッシュボードへ',
      loading: 'デバイス監視設定を読み込み中…',
      errorTitle: '設定の読み込みエラー',
      globalSettings: 'グローバルスケジューラー設定',
      tickIntervalLabel: 'スケジューラーティック間隔（秒）',
      tickIntervalHint: 'バックグラウンドジョブがポーリング対象の組織を確認するために起動する頻度です。最小の組織別ポーリング間隔以下に設定してください。',
      saveGlobal: 'グローバル設定を保存',
      savedGlobal: '保存済み — ジョブがライブで再スケジュールされました',
      orgsHeading: '組織',
      orgsSubheading: '— 組織をクリックして展開し、デバイスを設定',
      noOrgs: '組織が見つかりません。まずトポロジー同期を実行してください。',
      monitoringOn: '監視オン',
      monitoringOff: '監視オフ',
      pollIntervalLabel: 'ポーリング間隔（秒）',
      pollIntervalHint: '最小15秒。この組織の各ポーリングでMeraki APIが新しく呼び出されます。',
      devicesToMonitor: '監視対象デバイス',
      selectedCount: '（{count}件選択済み）',
      allNonExcluded: '（除外済み以外の全デバイス）',
      selectAll: '全て選択',
      deselectAll: '全て解除',
      loadingDevices: 'デバイスを読み込み中…',
      noDevices: 'この組織のデバイスが見つかりません。',
      deselectHint: '全て未選択のままにすると、この組織の除外済み以外の全デバイスを監視します。',
      save: '保存',
      saved: '保存済み',
      excluded: '除外済み',
      failedToSave: '保存に失敗しました',
      howItWorksTitle: '仕組み：',
      howItWorksText: 'スケジューラーはティック間隔ごとに起動し、対象の有効組織に対してMerakiに直接（キャッシュなし）ポーリングを行います。デバイスがオフラインになるとインシデントビューにアラートが発生し、',
      howItWorksAlertLink: 'アラート受信者',
      howItWorksText2: 'にメールが送信されます。デバイスがオンラインに戻ると自動的に解決されます。ライブネットワークビューで',
      howItWorksExcluded: '除外済み',
      howItWorksText3: 'としてマークされたデバイスは通知対象外です。',
    },

    // Resource Thresholds page (ja)
    resourceThresholds: {      title: 'リソース監視閾値',
      subtitle: 'アクセスポイント（AP）とスイッチの閾値を個別に設定します。デバイスが閾値を超えるとメールアラートが届きます。',
      back: '戻る',
      thresholdConfig: '閾値設定',
      accessPoints: 'アクセスポイント（MRシリーズ）',
      switches: 'L2スイッチ（MSシリーズ）',
      cpuUsage: 'CPU使用率（%）',
      memoryUsage: 'メモリ使用率（%）',
      maxClientsPerAP: 'AP当たりの最大クライアント数',
      interfaceUtilization: 'インターフェース使用率（%）',
      apCpuDesc: 'AP CPUがこの%を超えるとアラート',
      apMemoryDesc: 'APメモリがこの%を超えるとアラート',
      apClientDesc: 'クライアント数がこの上限を超えるとアラート',
      switchCpuDesc: 'スイッチCPUがこの%を超えるとアラート',
      switchMemoryDesc: 'スイッチメモリがこの%を超えるとアラート',
      switchTrafficDesc: 'インターフェース使用率がこの%を超えるとアラート',
      cooldownPeriod: 'アラートクールダウン期間（分）',
      cooldownDesc: 'メールスパムを防ぐためのアラート最小間隔',
      lastAlertSent: '前回のアラート送信：',
      saveThresholds: '閾値を保存',
      saving: '保存中...',
      saveSuccess: 'リソース閾値が正常に保存されました！',
      loading: 'リソース閾値を読み込み中...',
      notAuthenticated: '未認証',
      notAuthenticatedDesc: 'このページを表示するにはサインインしてください。',
      goToDashboard: 'ダッシュボードへ',
      errorTitle: '閾値の読み込みエラー',
      errorDesc: 'サインインしていること、およびバックエンドが実行中であることを確認してください。',
      noThresholds: '閾値が見つかりません。',
      howItWorks: 'システムの仕組み：',
      howItWorksDesc: 'AP（MRシリーズ）とスイッチ（MSシリーズ）を監視します。メモリ%はMeraki APIで取得。クライアント数はライブで取得。CPUはSNMPが必要（下記参照）。アラートはクールダウン期間を考慮します。メール受信者は',
      alertNotifications: 'アラート通知',
      alertNotificationsLink: 'で設定してください。',
      snmpTitle: 'SNMPによるCPU監視の有効化',
      snmpDesc: 'CPU データはMeraki Dashboard APIでは取得できません。有効にするには以下の手順に従ってください：',
      snmpStep1: 'Merakiダッシュボードでサービスを有効化：',
      snmpStep1Sub: 'ネットワーク全体 → 一般 → レポート → SNMPアクセス → 「許可」に設定',
      snmpStep2: 'コミュニティストリングをメモ（SNMPv2c用）またはSNMPv3認証情報を作成。',
      snmpStep3: '.envファイルに以下を追加：',
      snmpStep4: 'バックエンドを再起動 — 各デバイスのCPU%が自動的に表示されます。',
      snmpOids: '使用するOID：',
    },
  },

  en: {
    // Navigation
    nav: {
      dashboard: 'Dashboard',
      aiNocManager: 'AI NOC Manager',
      alerts: 'Alerts',
      aiDefenseLogs: 'AI Defense Logs',
      liveNetwork: 'Live Network',
      aiWorkbench: 'AI Workbench',
      settings: 'Setting',
    },

    // Header titles
    header: {
      dashboard: 'Dashboard',
      aiNocManager: 'AI NOC Manager',
      alerts: 'Alerts',
      settings: 'Settings',
      aiWorkbench: 'AI Workbench',
      liveNetwork: 'Live Network',
      aiDefenseLogs: 'AI Defense Logs',
    },

    // Language switcher
    language: {
      switchToEn: 'English',
      switchToJa: '日本語',
    },

    // Dashboard page
    dashboard: {
      greeting: 'Good Morning',
      overviewText: "Here's an overview of your workspace",
      export: 'Export',
      preparingExport: 'Preparing export...',
      exportSuccess: 'Dashboard data exported successfully!',
      exportError: 'Failed to export data',
      fetchingData: 'Fetching data for:',
      updatedTo: 'Updated to:',
      timeRanges: {
        today: 'Today',
        past7days: 'Past 7 days',
        past15days: 'Past 15 days',
        pastMonth: 'Past Month',
        pastYear: 'Past Year',
      },
    },

    // Insights/Alerts page
    insights: {
      loadingAlerts: 'Loading Alerts...',
      errorLoadingInsights: 'Error Loading Insights',
      activeIncidentsOverview: 'Active Incidents Overview',
      totalIncidents: 'Total Incidents:',
      solved: 'Solved',
      highPriority: 'High Priority',
      mediumPriority: 'Medium Priority',
      overviewFilterAll: 'All',
      overviewFilterNoc: 'NOC',
      overviewFilterSoc: 'SOC',
      activeIncidents: 'Active Incidents',
      triggerDemoAlerts: 'Trigger Demo Alerts',
      history: 'History',
      allStatus: 'All Status',
      new: 'New',
      peerReview: 'Peer Review',
      resolved: 'Resolved',
      falsePositive: 'False Positive',
      pendingApproval: 'Pending Approval',
      escalated: 'Escalated',
      approved: 'Approved',
      underObservation: 'Under Observation',
      device: 'Device',
      incident: 'Incident',
      activeSince: 'Ticket Created At',
      status: 'Status',
      aiPlan: 'AI Plan',
      actions: 'Actions',
      viewPlan: 'View Plan',
      aiTroubleshootingPlan: 'AI Troubleshooting Plan',
      sendReport: 'Send Report',
      generateWorkflow: 'generate workflow',
      executing: 'Executing...',
      noActiveIncidents: 'No active incidents found.',
      triggeringDemoAlerts: 'Triggering demo alerts...',
      demoAlertsTriggered: 'Demo alerts triggered! Refreshing incidents...',
      failedToTriggerDemo: 'Failed to trigger demo alerts.',
      statusUpdated: 'Incident status updated to',
      failedToUpdateStatus: 'Failed to update status.',
      pleaseSelectIncident: 'Please select at least one incident to view history',
      resolutionEmailSent: 'Resolution report email is being sent!',
      failedToSendEmail: 'Failed to send resolution report email.',
      workflowCompleted: 'Workflow completed successfully!',
      workflowFailed: 'Workflow execution failed',
      goTo: 'Go to:',
      failedToFetchAlerts: 'Failed to fetch alerts.',
      priorityColumnHeader: 'Priority',
      priorityCritical: 'Critical',
      priorityWarning: 'Warning',
      statusFilters: {
        all: 'All',
        new: 'New',
        inProgress: 'In Progress',
        resolved: 'Resolved',
        escalated: 'Escalated',
        underObservation: 'Under Observation',
      },
      timeSince: {
        yearsAgo: ' years ago',
        monthsAgo: ' months ago',
        daysAgo: ' days ago',
        hoursAgo: ' hours ago',
        minutesAgo: ' minutes ago',
        secondsAgo: ' seconds ago',
      },
      issueDescription: {
        tabLabel: 'Issue Description',
        aiPlanTab: 'AI Plan',
        whatHappened: 'What Happened',
        whenItHappened: 'When It Happened',
        siteAffected: 'Site Affected',
        clientsAffected: 'Clients Affected',
        impact: 'Impact',
        affectedIp: 'Affected IP',
        threatSummary: 'Threat Summary',
        raisedBy: 'Raised By',
        noDescription: 'No detailed issue description available.',
        noDescriptionSub: 'This incident was not reported via email or description data was not extracted.',
        // Meraki-specific
        organization: 'Organization',
        network: 'Network',
        deviceModel: 'Device Model',
        deviceMac: 'MAC Address',
        deviceTags: 'Device Tags',
        alertDetails: 'Alert Details',
        portNumber: 'Port Number',
        portDescription: 'Port Description',
        portStatus: 'Port Status',
        previousStatus: 'Previous Status',
        malwareHash: 'Malware Hash (SHA256)',
        powerSupplyNum: 'Power Supply Unit #',
        viewInMeraki: 'View in Meraki',
      },
    },

    // AI Action Modal
    aiModal: {
      initiateAction: 'Initiate Action: ',
      sendReport: 'Send Report',
      sendingReport: 'Sending Report...',
      workflow: 'Workflow',
      impactAssessment: 'Impact Assessment',
      resolution: 'Resolution',
      incidentDetails: 'Incident Details',
      approvalRequired: 'Approval Required',
      approvalRequiredDesc: 'You can review and customize this workflow, then submit it for approval by a Containment Approver. You cannot execute workflows directly.',
      aiRecommendedWorkflow: 'AI Recommended Workflow',
      aiRecommendedDescExecute: 'No policy found. The AI suggests the following plan. You can customize it before execution.',
      aiRecommendedDescSubmit: 'No policy found. The AI suggests the following plan. You can customize it before submission.',
      exactPolicyMatch: 'Exact Policy Match Found',
      executing: 'Executing',
      submitting: 'Submitting for review',
      standardProcedure: 'the standard procedure:',
      similarPolicyMatch: 'Similar Policy Match Found',
      policy: 'Policy:',
      confidence: 'Confidence:',
      matchType: 'Match type:',
      workflowBuilder: 'Workflow Builder',
      aiDeviceActions: 'AI Recommended Device Actions',
      aiDeviceActionsDesc: 'Based on the alert type and device, the AI suggests the following configuration changes. These are applied directly to the Meraki device.',
      risk: 'risk',
      applying: 'Applying...',
      applyNow: 'Apply Now',
      deviceWarning: 'These actions modify live Meraki device configuration. Review carefully before applying.',
      actionDetails: 'Action Details',
      actionParam: 'Action',
      editableParams: 'Editable Parameters',
      required: 'required',
      justification: 'Justification *',
      justificationPlaceholder: 'Explain why this workflow should be executed...',
      justificationHintApprover: 'Required for audit trail and documentation purposes.',
      justificationHintReviewer: 'This will be reviewed by an approver before execution.',
      confirmExecute: 'Type EXECUTE to confirm direct execution:',
      saveToAiPolicies: 'Save to AI Policies',
      submitForPeerReview: 'Submit for Peer Review',
      executeNow: 'Execute Now',
      submitForReview: 'Submit for Review',
      executingAction: 'Executing...',
      couldNotLoad: 'Could not load action plan.',
      savingPolicy: 'Saving policy...',
      initiatingAction: 'Initiating action...',
      submittingForReview: 'Submitting workflow for review...',
      impactTitle: 'Impact Assessment',
      currentImpactLevel: 'Current Impact Level',
      estimatedUsersAffected: 'Estimated Users Affected',
      impactReason: 'Impact Reason:',
      impactManualOverride: 'Impact level manually overridden',
      updateImpactLevel: 'Update Impact Level',
      overrideDesc: 'Override the automatic assessment if you have additional context about the incident impact.',
      high: 'High',
      medium: 'Medium',
      low: 'Low',
      impactUnavailable: 'Impact data unavailable',
      rootCause: 'Root Cause / Problem Analysis',
      stepsTaken: 'Steps Taken to Resolve',
      noStepsRecorded: 'No automated steps were recorded for this incident.',
      recurrencePrevention: 'Recurrence Prevention',
      sendResolutionReport: 'Send Resolution Report',
      sendingResolutionReport: 'Sending Report...',
      resolutionUnavailable: 'Resolution data unavailable',
      submittedForPeerReview: 'Workflow submitted for peer review! Awaiting approval.',
      submittedSuccessfully: 'Workflow submitted successfully!',
      actionInitiated: 'Action initiated! Run ID:',
      resolutionReportSent: 'Resolution report sent successfully',
      policySaved: 'Policy saved successfully!',
      policyNameRequired: 'Policy name is required',
      enterPolicyName: 'Enter a name for this policy:',
      justificationRequired: 'Please provide a justification for this action',
      impactLevelUpdated: 'Impact level updated to',
      stepAdded: 'Step added to workflow.',
      stepRemoved: 'Step removed from workflow.',
      unknown: 'Unknown time',
      by: 'by',
      // Workflow status banner
      workflowExecuting: 'Workflow Executing...',
      workflowCompletedSuccess: 'Workflow Completed Successfully',
      workflowExecutionFailed: 'Workflow Execution Failed',
      // HITL approval phase
      workflowPaused: 'Workflow Paused — Human Approval Required',
      workflowPausedDesc: 'Review the action below and approve or reject to continue.',
      whatHappenedSoFar: 'What happened so far',
      stepsCompleted: 'steps completed',
      pendingAction: 'Pending Action',
      parametersLabel: 'Parameters:',
      approveAndContinue: 'Approve & Continue',
      rejectAndStop: 'Reject & Stop',
      shareForApprovalEmail: 'Share for Approval via Email',
      approvalEmailSent: 'Approval request sent via email',
      // Pending review phase
      submittedForPeerReviewTitle: 'Submitted for Peer Review',
      submittedForPeerReviewDesc: 'An approver will review and execute this workflow.',
      // Execution history
      executionHistory: 'Execution History',
      executionLabel: 'Execution',
      waitingForSteps: 'Waiting for steps...',
      statusCompleted: 'Completed',
      statusFailed: 'Failed',
      statusRunning: 'Running...',
      statusAwaitingApproval: 'Awaiting Approval',
      // Save policy panel
      saveAsAiPolicy: 'Save as AI Policy',
      policyName: 'Policy Name',
      generatingTitle: 'Generating AI-powered title...',
      enterPolicyNamePlaceholder: 'Enter policy name',
      policyDescription: 'Description',
      policyDescriptionPlaceholder: 'Brief description of what this policy does',
      templateMode: 'Template Mode:',
      templateModeDesc: 'Real values will be replaced with placeholders:',
      savePolicy: 'Save Policy',
      // Impact level labels
      impactLevelUnknown: 'Unknown',
    },

    // Event Action History Modal
    historyModal: {
      actionHistoryFor: 'Action History for',
      description: 'A chronological log of all workflow executions in response to this event.',
      noExecutions: 'No workflow executions have been logged for this incident yet.',
      failedToLoad: 'Failed to load workflow execution history.',
      incident: 'Incident #',
      completed: 'Completed',
      failed: 'Failed',
      steps: 'steps',
      success: 'success',
      target: 'Target:',
      viewDetails: 'View Details',
      parameters: 'Parameters:',
      result: 'Result:',
    },

    // Settings page
    settings: {
      knowledgeManagement: 'Knowledge Management',
      knowledgeManagementDesc: 'Upload and manage documents for the AI knowledge base',
      alertNotifications: 'Alert Notifications',
      alertNotificationsDesc: 'Configure automated email alerts and recipients',
      resourceMonitoring: 'Resource Monitoring',
      resourceMonitoringDesc: 'Set CPU, memory, session, and traffic thresholds for email alerts',
      aiPolicies: 'AI Policies',
      aiPoliciesDesc: 'Create automated incident response workflows using natural language',
      customTools: 'Custom Tools',
      customToolsDesc: 'Create custom API tools for the chatbot',
      securitySettings: 'Security Settings',
      securitySettingsDesc: 'Manage authentication, API keys, and access controls',
      integrations: 'Integrations',
      integrationsDesc: 'Connect Outlook email accounts for NOC alerts and SOC command listening',
      comingSoon: 'Coming Soon',
      proTip: 'Configure your settings to optimize the AI NOC Manager for your specific network environment and operational needs.',
      proTipLabel: '💡 Pro Tip:',
    },

    // Live Network page
    liveNetwork: {
      devices: 'DEVICES',
      recentEvents: 'RECENT EVENTS',
      eventHistory: 'EVENT HISTORY',
      correlation: 'CORRELATION',
      totalEvents: 'Total Events',
      eventBursts: 'Event Bursts',
      problemDevices: 'Problem Devices',
      potentialRootCauses: 'Potential Root Causes',
      commonPatterns: 'Common Patterns',
      recentEventsCount: 'Recent Events',
      eventHistoryWithAI: 'Event History with AI Analysis',
      noRecentEvents: 'No recent events',
      noHistoricalEvents: 'No historical events',
      failedToLoad: 'Failed to load network details. Please try again.',
      selectNetwork: 'Select a network from the sidebar to view details.',
      aiAnalysis: 'AI Analysis:',
      device: 'Device:',
    },

    // Workbench page
    workbench: {
      aiWorkbench: 'AI Workbench',
      containmentProposals: 'Containment Proposals',
      approveAll: 'Approve All',
      denyAll: 'Deny All',
      approve: 'Approve',
      deny: 'Deny',
      activeContainments: 'Active Containments',
      auditLogs: 'Audit Logs',
      noProposals: 'No proposals',
      noContainments: 'No containments',
      noLogs: 'No logs',
      action: 'Action',
      target: 'Target',
      timestamp: 'Timestamp',
      user: 'User',
      result: 'Result',
    },

    // Security Events page
    securityEvents: {
      title: 'AI Defense Logs',
      noEvents: 'No security events',
      eventDetails: 'Event Details',
      violations: 'Violations',
      messages: 'Messages',
      copyId: 'Copy ID',
      copied: 'Copied!',
    },

    // Manager page
    manager: {
      title: 'AI NOC Manager',
    },

    // NOC Agent Status Card (Circuit Breaker)
    nocStatus: {
      nocAgent: 'NOC Agent',
      circuitBroken: 'Circuit Broken',
      circuitBreakResolved: 'Circuit Break Resolved',
      sessionRestored: 'Circuit Break Resolved — Session Restored',
      operational: 'Operational — CB Tunnel Active',
      liveSessionInactive: 'Live session inactive — reconnecting…',
      isolatedBySOC: 'Isolated by SOC command',
      triggeredBy: 'By:',
      triggeredAt: 'At:',
      publicIp: 'IP:',
      markAsResolved: 'Mark as Resolved',
      appsLabel: 'Apps',
      hideBeforeAfter: 'Hide before / after',
      showBeforeAfter: 'Show before / after',
      before: 'Before',
      after: 'After',
      active: 'Active',
      terminated: 'Terminated',
      toastResolvedTitle: 'Circuit Break Resolved',
      toastResolvedDesc: 'NOC Agent confirmed reconnected — audit record closed.',
      toastAlreadyResolvedTitle: 'Already Resolved',
      toastAlreadyResolvedDesc: 'No active circuit break found.',
    },

    // SOC Isolation Page
    nocIsolation: {
      pageTitle: 'NOC Agent Isolation',
      pageDesc: 'Circuit breaker control for the NOC AI Employee',
      refresh: 'Refresh',
      deviceMac: 'Device MAC',
      isolatedAt: 'Isolated At',
      triggeredBy: 'Triggered By',
      lastSeen: 'Last Seen',
      triggeringEmail: 'Triggering Email',
      resources: 'Resources',
      blocked: 'BLOCKED',
      ok: 'OK',
      resourceUnavailable: 'Resource status unavailable',
      manualCircuitBreak: 'Manual Circuit Break',
      reasonLabel: 'Reason *',
      reasonPlaceholder: 'e.g., Abnormal traffic spike, Manual operator decision...',
      incidentRefLabel: 'Incident Ref (optional)',
      incidentRefPlaceholder: 'e.g., INC-12345',
      isolateBtn: 'Isolate NOC Agent',
      isolationTimeline: 'Isolation Timeline',
      noEventsYet: 'No isolation events yet',
      circuitBroken: 'Circuit Broken',
      restored: 'Restored',
      timestamp: 'Timestamp',
      cbResponse: 'CB Response',
      emailExcerpt: 'Email Excerpt',
      appsBeforeAfter: 'Apps (Before / After)',
      hideApps: 'Hide apps',
      showApps: 'Show apps',
      appName: 'App',
      before: 'Before',
      after: 'After',
      active: 'Active',
      terminated: 'Terminated',
      accessible: 'Accessible',
      circuitOperational: 'Operational',
      loading: 'Loading…',
      loadingHistory: 'Loading history…',
      toastIsolated: 'NOC Agent Isolated',
      toastValidation: 'Please provide a reason for isolation',
      toastIsolationFailed: 'Isolation Failed',
      justNow: 'just now',
      minutesAgo: 'm ago',
      hoursAgo: 'h ago',
      daysAgo: 'd ago',
      nocAgentLabel: 'NOC Agent',
      appsCount: 'apps',
      triggerSource: {
        EMAIL_COMMAND: 'Email Command',
        DSPA_SCRIPT: 'DSPA Script',
        MANUAL_UI: 'Manual UI',
        MANUAL_VERIFY: 'Manual Verify',
        DSPA_TRAFFIC_SPIKE: 'DSPA Traffic Spike',
        DSPA_AUTO: 'DSPA Auto',
      },
    },

    // Alerts page — tab labels & SOC circuit break card
    alertsPage: {
      nocAlertsTab: 'NOC Alerts',
      socAlertsTab: 'SOC Alerts',
      loadingSocAlerts: 'Loading SOC alerts…',
      noSocAlerts: 'No SOC circuit-break commands received',
      noSocAlertsDesc: 'Commands arrive when an analyst sends an email containing "circuit break"',
      circuitRestored: 'Circuit Restored',
      circuitBroken: 'Circuit Broken',
      triggerEmail: 'Email Command',
      triggerBandwidth: 'Bandwidth Spike',
      triggerManual: 'Manual',
      receivedFrom: 'Received from',
      workflow: 'Workflow',
      stepEmailReceived: 'Email received',
      stepCircuitBreakInitiated: 'Circuit break initiated',
      stepRestored: 'Restored',
      markAsResolved: 'Mark as Resolved',
      markAsResolvedDesc: 'Click to confirm the NOC Agent has reconnected and close this audit record.',
      resolvedToast: 'Circuit break resolved — NOC Agent session confirmed reconnected',
      resolveFailToast: 'Failed to resolve circuit break — please try again',
      hideApps: 'Hide apps',
      viewApps: 'View apps ({count} affected)',
      appsTotal: 'Apps (total {count})',
      appsLiveBadge: 'Live',
      appsRefresh: 'Refresh',
      appsFetchingLive: 'Fetching from CloudBrink…',
      appsLiveError: '— showing stored snapshot',
      appsColName: 'Name',
      appsColBefore: 'Before',
      appsColAfter: 'After',
      appsStatusActive: 'Active',
      appsStatusTerminated: 'Terminated',
      appsPublicIp: 'NOC public IP:',
      appsSnapshot: 'snapshot:',
      justNow: 'just now',
      minutesAgo: 'm ago',
      hoursAgo: 'h ago',
      daysAgo: 'd ago',
    },

    // Ask NOC Manager / Chatbot
    askNoc: {
      welcomeTitle: 'Welcome to the AI NOC Manager! 👋',
      welcomeText: "I'm your intelligent assistant, here to help you with network operations.",
      placeholder: 'Type your question...',
      placeholderFull: 'Type your question here...',
      addQuestion: 'Add question...',
      fetchingData: 'Fetching data...',
      errorMessage: 'Sorry, I encountered an issue connecting to the AI.',
      suggestedQuestions: [
        'List all networks',
        'Check network spike',
        'What are the recent critical alerts and how to troubleshoot them?',
        'Show network health summary',
        'Which APs have poor signal strength and create a report of it',
      ],
      // Spike test
      spikeConnecting: 'Connecting…',
      spikeDownloading: 'Downloading…',
      spikeDownloadComplete: '✓ Download complete',
      spikeDownloadFailed: '✗ Download failed',
      spikeMbDownloaded: 'MB downloaded',
      spikeMbRemaining: 'MB remaining',
      spikeStatsFormat: '{elapsed}s / 60s',
      spikeConnectionInterrupted: 'Download failed — connection was interrupted.',
      spikeDownloadCompletedMsg: 'Download completed.',
      spikeSessionTerminatedCallout: 'Failed to download. The NOC session was terminated by the circuit breaker, so the file could not be completed.',
      spikeDownloadingForTest: 'Downloading file for network spike test…',
    },

    // Common
    common: {
      loading: 'Loading...',
      error: 'Error',
      retry: 'Retry',
      close: 'Close',
      save: 'Save',
      cancel: 'Cancel',
      confirm: 'Confirm',
      delete: 'Delete',
      edit: 'Edit',
      add: 'Add',
      search: 'Search',
      filter: 'Filter',
      export: 'Export',
      import: 'Import',
      refresh: 'Refresh',
      back: 'Back',
      next: 'Next',
      previous: 'Previous',
      submit: 'Submit',
      reset: 'Reset',
      unknown: 'Unknown',
      unknownDevice: 'Unknown Device',
      unknownIncident: 'Unknown Incident',
      na: 'N/A',
    },

    // KPI / dashboard widgets
    kpi: {
      highPriority: 'High Priority',
      mediumPriority: 'Medium Priority',
      resolved: 'Resolved',
      filterByNetwork: 'Filter by Network',
      allNetworks: 'All Networks',
      lockFilter: 'Lock Filter',
      unlockFilter: 'Unlock Filter',
      setHighPriority: 'Set High Priority',
      setMediumPriority: 'Set Medium Priority',
      breakdownByNetwork: 'Breakdown by Network',
      sourceAll: 'All',
      sourceNoc: 'NOC',
      sourceSoc: 'SOC',
      summaryLabel: 'Alert Status',
      summaryStatusActive: 'Active',
      summaryStatusAllClear: 'All Clear',
      summaryStatusNoAlerts: 'No Alerts',
      summaryAllClear: 'All clear! {resolved} incident(s) resolved in this period.',
      summaryNoAlerts: 'No active alerts at this time.',
      summaryHighPrefix: 'High priority: {count}',
      summaryMediumPrefix: 'Medium priority: {count}',
      summaryResolvedSuffix: 'Resolved: {count}',
      summaryAnd: 'and',
      summaryAffectedNetwork: 'Concentrated on {network}',
      summaryAlertTypes: 'Alert types:',
    },

    // Network health
    networkHealth: {
      title: 'Network Health Score',
      online: 'Online',
      alerting: 'Alerting',
      offline: 'Offline',
      dormant: 'Dormant',
    },

    // Organization Metrics widget
    organizationMetrics: {
      sectionTitle: 'Network Uptime & Active Clients',
      allNetworks: 'All Networks',
      networkUptime: 'Network Uptime',
      totalActiveClients: 'Total Active Clients',
      totalActiveClientsScope: '(Wired + Wireless)',
      loading: 'Loading…',
      devicesOnline: 'of',
      devicesOnlineSuffix: 'devices online',
      across: 'Across',
      networks: 'networks',
    },

    // Resource Monitoring widget badge labels
    resourceMonitoringLabels: {
      subtitle: 'AP (Wireless Clients Only) · Switch · CPU · Memory',
      clientsThisNetwork: 'AP Wireless Clients (this network)',
      allNetworks: 'AP Wireless Clients (all networks)',
      wirelessOnly: '* Wireless AP clients only — excludes wired',
    },

    // Client Top Talkers widget
    clientTopTalkers: {
      title: 'Client Top Talkers',
      subtitle: '— bandwidth per client (real-time)',
      sent: 'Sent',
      received: 'Received',
      loading: 'Loading client usage…',
      noData: 'No client usage data available',
      noDataSub: 'No active clients found',
      totalClients: 'total clients',
      aboveAverage: '× above average usage',
      os: 'OS',
      ssid: 'SSID',
      network: 'Network',
    },

    // Device Fleet widget
    deviceFleet: {
      title: 'Device Fleet',
      subtitle: '— live status by product family',
      online: 'Online',
      dormant: 'Dormant',
      alerting: 'Alerting',
      offline: 'Offline',
      totalDevices: 'total devices',
      loading: 'Loading device fleet…',
      noData: 'No device data available',
      total: 'Total',
    },

    // Network Overview Cards (Live Network page)
    networkOverviewCards: {
      aiHealthScore: 'AI Health Score',
      keyInsights: 'KEY INSIGHTS:',
      activeClientsConnected: 'active clients connected',
      refreshing: 'Refreshing...',
      refresh: '🔄 Refresh',
      networkOverview: 'Network Overview',
      networkId: 'NETWORK ID:',
      timeZone: 'TIME ZONE:',
      activeClients: 'ACTIVE CLIENTS:',
      productTypes: 'PRODUCT TYPES:',
      orgId: 'Org ID:',
      trafficSummary: 'Traffic Summary (Last Hour)',
      sent: 'SENT:',
      received: 'RECEIVED:',
      total: 'Total:',
      healthy: 'HEALTHY',
      warning: 'WARNING',
      critical: 'CRITICAL',
      failedToRefresh: 'Failed to refresh health summary. Please try again.',
    },

    // Network Context Selector
    networkContextPanel: {
      title: 'Network Context',
      subtitle: 'Select a network to monitor',
      searchPlaceholder: 'Search',
      noNetworksFound: 'No networks found',
    },

    // Devices Status Table
    devicesStatus: {
      title: 'Devices Status',
      excluded: 'Excluded',
      device: 'Device',
      name: 'NAME',
      model: 'MODEL',
      serial: 'SERIAL',
      status: 'STATUS',
      actions: 'ACTIONS',
      noDevices: 'No devices found',
      restoreMonitoring: 'Restore Monitoring',
      markAsExcluded: 'Mark as Excluded',
      excludeTitle: 'Mark Device as Excluded',
      excludeDesc: 'This device will be excluded from failure detection and the AI Health Score. It will remain visible in the network view with an "Excluded" status.',
      reasonLabel: 'Reason (optional)',
      reasonPlaceholder: 'e.g. Scheduled maintenance, decommissioned, lab device…',
      confirmExclusion: 'Confirm Exclusion',
      saving: 'Saving…',
      restoreTitle: 'Restore Device to Monitoring',
      restoreDesc: 'This device will be removed from the excluded list and re-included in failure detection and the AI Health Score.',
      restoring: 'Restoring…',
      exclusionDetails: 'Exclusion Details',
      serialLabel: 'Serial:',
      reasonDetailLabel: 'Reason:',
      excludedByLabel: 'Excluded by:',
      excludedAtLabel: 'Excluded at:',
      viewExclusionDetails: 'View exclusion details',
    },

    // Workbench page (extended)
    workbenchPage: {
      analyzing: 'AI Strategy Agent is analyzing...',
      proposeContainment: 'Propose Containment',
      network: 'Network:',
      aiRecommendedStrategy: 'AI-Recommended Containment Strategy',
      aiRecommendation: 'AI Recommendation',
      reason: 'Reason:',
      selectStrategy: 'Select Containment Strategy',
      isolateAllClients: 'Isolate All Clients',
      isolateAllClientsDesc: 'Block all client traffic on this network',
      isolateSwitchPort: 'Isolate Specific Switch Port',
      isolateSwitchPortDesc: 'Isolate a single wired device port',
      isolateWirelessSSID: 'Isolate Wireless SSID',
      isolateWirelessSSIDDesc: 'Isolate a specific wireless network',
      isolateVLAN: 'Isolate Entire VLAN',
      isolateVLANDesc: 'Last resort for widespread incidents',
      justification: 'Justification (Pre-filled by AI)',
      justificationPlaceholder: 'Explain why this containment is necessary',
      adminDirectExecution: 'Admin Direct Execution',
      adminDirectExecutionDesc: 'You are about to execute containment immediately.',
      typeIsolate: "Type 'ISOLATE' to confirm execution",
      executeNow: 'Execute Now',
      submitForReview: 'Submit for Review',
      cancel: 'Cancel',
      currentlyIsolatedNetworks: 'Currently Isolated Networks',
      noNetworksIsolated: 'No networks are currently isolated.',
      pendingApprovals: 'Pending Approvals',
      pendingApprovalsDesc: 'Review and approve workflow executions and containment requests from your team.',
      noPendingProposals: 'No pending proposals.',
      approvedWorkflows: 'Approved Workflows - Ready to Execute',
      approvedWorkflowsDesc: 'These workflows have been approved and are ready for execution.',
      noApprovedWorkflows: 'No approved workflows awaiting execution.',
      auditTrail: 'Containment Audit Trail',
      auditTrailDesc: 'Complete history of all containment actions.',
      noAuditTrail: 'No audit trail available.',
      auditTrailNote: 'Audit logs will appear here after containment actions are performed.',
      timestamp: 'Timestamp',
      action: 'Action',
      networkCol: 'Network',
      details: 'Details',
      proposedBy: 'Proposed by:',
      reviewedBy: 'Reviewed by:',
      comment: 'Comment:',
      checkStatus: 'Check Status',
      release: 'Release',
      containedBy: 'Contained by',
      proposedByOn: 'Proposed by ',
      on: ' on ',
      approvedBadge: 'Approved',
      youCanExecute: 'You can execute this workflow',
      onlyCanExecute: 'Only {name} can execute',
      executing: 'Executing...',
      execute: 'Execute',
      approveContainment: 'Approve Containment Proposal',
      denyContainment: 'Deny Containment Proposal',
      reviewComment: 'Review Comment (Required)',
      reviewPlaceholder: 'Provide a reason for approving/denying this proposal...',
      confirmApprove: 'Confirm Approve',
      confirmDeny: 'Confirm Deny',
      noPermission: 'You do not have permission to view this page.',
      noPermissionSection: 'You do not have permission to view this section.',
      noPermissionAudit: 'You need the "Containment Initiator" or "Containment Approver" role to view the audit trail.',
      tabActive: 'Active Action',
      tabPending: 'Pending Approval',
      tabApproved: 'Ready to Execute',
      tabAudit: 'Audit Trail',
      submittingRequest: 'Submitting containment request...',
      requestSubmitted: 'Containment request submitted for review!',
      executingContainment: 'Executing containment...',
      executionSuccess: 'Containment executed successfully!',
      releasingNetwork: 'Releasing network...',
      releaseSuccess: 'Network release initiated.',
      executingWorkflow: 'Executing workflow...',
      confirmRelease: 'Are you sure you want to release network "{name}"?',
      confirmExecuteWorkflow: 'Are you sure you want to execute this workflow?',
      testing: 'Testing',
    },

    // Knowledge Management page
    knowledge: {
      title: 'Knowledge Management',
      subtitle: 'Upload and manage documents for the AI knowledge base',
      back: 'Back',
      uploadDocument: 'Upload Document',
      uploadedDocuments: 'Uploaded Documents',
      clickToUpload: 'Click to upload',
      orDragAndDrop: 'or drag and drop',
      nativeFormats: 'PDF, DOCX, PPTX, XLSX, MP3, MP4, WAV, EML, MSG (native)',
      autoConvertFormats: 'PNG, JPG, GIF, JSON, TXT, CSV, HTML, MD and more — auto-converted on upload',
      maxSize: 'Max 64 MB',
      uploading: 'Uploading...',
      documentName: 'Document Name',
      status: 'Status',
      actions: 'Actions',
      statusReady: 'Ready',
      statusProcessing: 'Processing',
      statusFailed: 'Failed',
      noDocuments: 'No documents uploaded yet',
      noDocumentsSub: 'Upload your first document to build the knowledge base',
      deleteConfirm: 'Are you sure you want to delete "{name}"?',
      uploadSuccess: 'Successfully uploaded "{name}"',
      uploadSuccessNote: 'Successfully uploaded "{name}". Note:',
      deleteSuccess: 'Successfully deleted "{name}"',
      fileTooLarge: 'File size must be less than 64 MB',
      cannotDeleteProcessing: 'Cannot delete while processing',
      deleteDocument: 'Delete document',
    },

    // Integrations page
    integrations: {
      back: 'Back',
      title: 'Integrations',
      subtitle: 'Connect Outlook email accounts to enable NOC alert notifications and SOC command listening.',
      checking: 'Checking…',
      connected: 'Connected',
      notConnected: 'Not Connected',
      loadingStatus: 'Loading status…',
      tokenValid: 'Token valid',
      autoRefreshes: 'auto-refreshes',
      tokenExpiredFor: 'Token expired for',
      tokenExpiredSuffix: '— reconnect to restore',
      noAccountConnected: 'No Outlook account connected. Click Connect to authorise via Microsoft OAuth.',
      listeningFolder: 'Listening folder',
      subfolderInMailbox: 'subfolder in the connected mailbox',
      outboundNotifications: 'Outbound notifications',
      outboundDesc: 'Alert emails are sent from the connected mailbox to recipients in Alert Settings',
      inboundBehaviour: 'Inbound behaviour',
      inboundNoc: 'Incoming emails → parsed as Network Events and Containment Proposals',
      inboundSoc: 'Incoming emails → parsed for circuit-break, suspend, or release commands',
      reconnectAccount: 'Reconnect Account',
      connectAccount: 'Connect Account',
      refreshStatus: 'Refresh Status',
      socDisabledReason: 'SOC analyst role required to manage SOC Outlook integration',
      footerNote: 'Note: ',
      footerText: 'Both NOC and SOC use the same physical mailbox. The folder name controls which process handles each incoming email.',
      footerNocFolder: 'Emails in the NOC folder are processed as network incidents;',
      footerSocFolder: 'emails in the SOC folder are parsed as analyst commands.',
      outlookConnectedSuccess: 'Outlook connected successfully',
      outlookConnectionFailed: 'Outlook connection failed',
      remaining: 'remaining',
    },

    // SOC Alerts page
    socAlerts: {
      title: 'Alerts',
      subtitle: 'NOC network events and SOC circuit-break commands',
      refresh: 'Refresh',
      tabNocMeraki: 'NOC — Meraki',
      tabNocEmail: 'NOC — Email',
      tabSocCircuitBreak: 'SOC — Circuit Break',
      tabSocSuspensions: 'SOC — Suspensions',
      activeSuspension: 'active suspension',
      activeSuspensions: 'active suspensions',
      merakiWebhookAlerts: 'Meraki Webhook Alerts',
      emailSourcedAlerts: 'Email-Sourced NOC Alerts',
      socCircuitBreakAll: 'SOC Circuit Break — All Events',
      symphonyTrustSuspensions: 'Symphony Trust — NOC Suspension Events',
      noMerakiAlerts: 'No Meraki webhook alerts',
      noEmailAlerts: 'No email-sourced alerts',
      noCircuitBreakEvents: 'No circuit break events recorded',
      noSuspensionEvents: 'No suspension events recorded',
      aiPlan: 'AI plan:',
      by: 'By:',
      viaEmailCommand: 'via email command',
      fromSocDashboard: 'from the SOC dashboard',
      agentsSuspended: 'Agents suspended',
      agentSuspended: 'Agent suspended',
      aiEmployeeSuspended: 'AI Employee suspended',
      fullOperationDetails: 'Full operation details',
      resumedBy: 'Resumed by',
      resumeRestoreAgents: 'Resume & Restore Agents',
      resumingAgents: 'Resuming agents & re-issuing credentials…',
      verifyingAgents: 'Verifying agent status & credentials…',
      restored: 'Restored',
      circuitBroken: 'Circuit Broken',
      triggerSourceEmail: 'Email',
      triggerSourceManual: 'Manual',
      triggerSourceDspa: 'DSPA',
      triggerSourceVerify: 'Verify',
      triggerSourceOutlook: 'Outlook',
      triggerSourceCiscoAI: 'Cisco AI Defense',
      fromCiscoAiDefense: 'detected by Cisco AI Defense',
      cbSourceEmail: 'Email Command',
      cbSourceOutlook: 'Outlook Email',
      cbSourceManual: 'Manual',
      cbSourceDspa: 'DSPA Alert',
      cbSourceVerify: 'Session Verify',
      suspended: 'SUSPENDED',
      resumed: 'RESUMED',
      scope: 'Scope:',
      triggeredBy: 'Triggered by',
      suspensionEventTitle: 'Suspension Event #',
      fullOpLog: 'Full operation log for all affected entities',
      noEntityDetails: 'No entity details recorded',
      resumeRestoreAll: 'Resume & Restore All Agents',
      resuming: 'Resuming…',
      verifying: 'Verifying…',
      accessRestored: 'Access restored',
      resumedWithWarnings: 'Resumed with warnings',
      resumeFailed: 'Resume failed',
      allAgentsRestored: 'All agents restored and verified',
      someAgentsNeedAttention: 'Resumed — some agents need attention',
      justNow: 'just now',
      minutesAgo: 'm ago',
      hoursAgo: 'h ago',
      daysAgo: 'd ago',
      loading: 'Loading…',
    },

    // Cisco AI Defense page
    ciscoDefense: {
      title: 'Cisco AI Defense Runtime Events',
      subtitle: 'Security events tracked by Cisco AI Defense Gateway · Click a row to see blocked content',
      live: 'Live · 30s',
      total: 'Total:',
      page: 'Page:',
      filtered: 'Filtered:',
      allActions: 'All Actions',
      block: 'Block',
      allow: 'Allow',
      reset: 'Reset',
      exportCsv: 'Export CSV',
      exportSuccess: 'Exported successfully',
      exportFailed: 'Export failed',
      colDateTime: 'Date & Time',
      colAction: 'Action',
      colApplicationId: 'Application ID',
      colPolicyId: 'Policy ID',
      colEventId: 'Event ID',
      failedToFetch: 'Failed to fetch events',
      retry: 'Retry',
      noEventsFound: 'No events found.',
      showing: 'Showing',
      of: 'of',
      previous: 'Previous',
      next: 'Next',
      copyFullId: 'Copy full ID',
      copied: 'Copied!',
      eventDetail: 'Event Detail',
      couldNotLoadDetail: 'Could not load event details.',
      failedToLoadDetail: 'Failed to load event details',
      eventInfo: 'Event Info',
      date: 'Date',
      action: 'Action',
      eventId: 'Event ID',
      applicationId: 'Application ID',
      policyId: 'Policy ID',
      connectionId: 'Connection ID',
      whyBlocked: 'Why it was Blocked',
      allChecksRun: 'All Checks Run',
      blockedContent: 'Blocked Content',
      noContentAvailable: '(no content available)',
    },

    // Device Power Monitoring settings page (en)
    deviceMonitoring: {
      title: 'Device Power Monitoring',
      subtitle: 'Configure which organizations and devices to monitor for power-off events. An alert is raised when a monitored device goes offline and auto-resolved when it comes back.',
      back: 'Back',
      notAuthenticated: 'Not authenticated',
      notAuthenticatedDesc: 'Please sign in to configure device monitoring.',
      goToDashboard: 'Go to Dashboard',
      loading: 'Loading device monitoring settings…',
      errorTitle: 'Error loading settings',
      globalSettings: 'Global Scheduler Settings',
      tickIntervalLabel: 'Scheduler tick interval (seconds)',
      tickIntervalHint: 'How often the background job wakes up to check which organizations are due for a poll. Should be ≤ the smallest per-org poll interval.',
      saveGlobal: 'Save global settings',
      savedGlobal: 'Saved — job rescheduled live',
      orgsHeading: 'Organizations',
      orgsSubheading: '— click an org to expand and configure devices',
      noOrgs: 'No organizations found. Run a topology sync first.',
      monitoringOn: 'Monitoring on',
      monitoringOff: 'Monitoring off',
      pollIntervalLabel: 'Poll every (seconds)',
      pollIntervalHint: 'Minimum 15 seconds. Meraki API is called fresh on each poll for this org.',
      devicesToMonitor: 'Devices to monitor',
      selectedCount: '({count} selected)',
      allNonExcluded: '(all non-excluded)',
      selectAll: 'Select all',
      deselectAll: 'Deselect all',
      loadingDevices: 'Loading devices…',
      noDevices: 'No devices found for this organization.',
      deselectHint: 'Leave all deselected to monitor all non-excluded devices in this org.',
      save: 'Save',
      saved: 'Saved',
      excluded: 'Excluded',
      failedToSave: 'Failed to save',
      howItWorksTitle: 'How it works: ',
      howItWorksText: 'The scheduler wakes up every tick interval and polls Meraki directly (no cache) for each enabled org that is due. When a device goes offline an alert is raised in the Incidents view and an email is sent to your ',
      howItWorksAlertLink: 'alert recipients',
      howItWorksText2: '. The alert is automatically resolved when the device comes back online. Devices marked as ',
      howItWorksExcluded: 'Excluded',
      howItWorksText3: ' in the Live Network view are never alerted on.',
    },

    // Resource Thresholds page (en)
    resourceThresholds: {
      title: 'Resource Monitoring Thresholds',
      subtitle: 'Configure separate threshold values for Access Points (APs) and Switches. You\'ll receive email alerts when individual devices exceed their thresholds.',
      back: 'Back',
      thresholdConfig: 'Threshold Configuration',
      accessPoints: 'Access Points (MR Series)',
      switches: 'L2 Switches (MS Series)',
      cpuUsage: 'CPU Usage (%)',
      memoryUsage: 'Memory Usage (%)',
      maxClientsPerAP: 'Max Clients per AP',
      interfaceUtilization: 'Interface Utilization (%)',
      apCpuDesc: 'Alert when AP CPU exceeds this %',
      apMemoryDesc: 'Alert when AP memory exceeds this %',
      apClientDesc: 'Alert when client count exceeds this limit',
      switchCpuDesc: 'Alert when switch CPU exceeds this %',
      switchMemoryDesc: 'Alert when switch memory exceeds this %',
      switchTrafficDesc: 'Alert when interface utilization exceeds this %',
      cooldownPeriod: 'Alert Cooldown Period (minutes)',
      cooldownDesc: 'Minimum time between repeated alerts to prevent email spam',
      lastAlertSent: 'Last alert sent:',
      saveThresholds: 'Save Thresholds',
      saving: 'Saving...',
      saveSuccess: 'Resource thresholds saved successfully!',
      loading: 'Loading resource thresholds...',
      notAuthenticated: 'Not Authenticated',
      notAuthenticatedDesc: 'Please sign in to view this page.',
      goToDashboard: 'Go to Dashboard',
      errorTitle: 'Error Loading Thresholds',
      errorDesc: 'Ensure you are signed in and the backend is running.',
      noThresholds: 'No thresholds found.',
      howItWorks: 'How it works:',
      howItWorksDesc: 'The system monitors your APs (MR series) and Switches (MS series). Memory % is fetched via Meraki API. Client counts are fetched live. CPU requires SNMP (see below). Alerts respect the cooldown period. Configure email recipients in',
      alertNotifications: 'Alert Notifications',
      alertNotificationsLink: '.',
      snmpTitle: 'Enable CPU Monitoring via SNMP',
      snmpDesc: 'CPU data is not available via the Meraki Dashboard API. To enable it, follow these steps:',
      snmpStep1: 'Enable SNMP on Meraki Dashboard:',
      snmpStep1Sub: 'Network-wide → General → Reporting → SNMP Access → Set to "Allow"',
      snmpStep2: 'Note your Community String (for SNMPv2c) or create SNMPv3 credentials.',
      snmpStep3: 'Add these variables to your .env file:',
      snmpStep4: 'Restart the backend — CPU % will automatically appear for each device.',
      snmpOids: 'OIDs used:',
    },
  },
} as const

export type TranslationKeys = typeof translations.en
